import type { TranslationKey } from '@heidi/runtime/i18n';
import type { LucideIcon } from 'lucide-react';
import { FileText, Pen, Pill, Search, Sparkles, Stethoscope } from 'lucide-react';

export type EvidenceVerbId = 'lookUp' | 'workUp' | 'treat' | 'prepare' | 'write' | 'fill';

export interface EvidenceVerbSuggestionItem {
  /** Unique identifier for the item within its verb */
  id: string;
  /** i18n key for the pattern label shown in the popover (e.g. "Check dosing of a drug") */
  patternLabelKey: TranslationKey;
  /** i18n key for the hover preview with placeholder badges (e.g. "What is the dosing of [drug]?") */
  hoverPreviewKey: TranslationKey;
  /** i18n key for the starter text populated on click (e.g. "What is the dosing of ") */
  starterKey: TranslationKey;
  /** Region filter: US-only, AU-only, or global */
  locale: 'US' | 'AU' | 'global';
  /** Optional trigger character appended to starter to auto-open an autocomplete menu (@=patient, #=session) */
  triggerSuffix?: '@' | '#';
  /**
   * Retrieval-oriented copy. The in-session experiment uses this for Treat
   * items in every market. The Evidence homepage uses it for every item that
   * has one when the clinician's country restricts Treat wording.
   */
  nonUsVariant?: {
    patternLabelKey: TranslationKey;
    hoverPreviewKey: TranslationKey;
    starterKey: TranslationKey;
  };
}

export interface EvidenceVerb {
  /** Unique verb identifier */
  id: EvidenceVerbId;
  /** i18n key for the verb chip label */
  labelKey: TranslationKey;
  /** Icon shown in chip and popover header */
  icon: LucideIcon;
  /** Curated suggestion items for this verb (4-5) */
  items: EvidenceVerbSuggestionItem[];
}

/**
 * Five-verb homepage suggestion model.
 *
 * Each verb maps to a bucket of clinician intent (see 02-jtbd-taxonomy.md).
 * Items are curated suggestions that demonstrate Evidence capabilities,
 * mixing questions and tool-call actions.
 *
 * Content principles (see 05-content-principles-and-revised-suggestions.md):
 * 1. "Busy Tuesday" test — must resonate with GP / ED doctors
 * 2. Specific but universal — real clinical moment, broad applicability
 * 3. Natural clinical voice — how clinicians talk, not exam stems
 * 4. Possible on a blank homepage — first message in a new chat
 * 5. Show breadth, not depth — 13 specialties, no condition repeated
 * 6. One capability per verb — no duplicate patterns
 *
 * Verbs: Look up, Work up, Treat, Prepare, Write
 * - Look up: Clinical Q&A + drug safety + library search
 * - Work up: Diagnostics, differentials, calculators, result interpretation
 * - Treat: Treatment decisions, comparisons, escalation
 * - Prepare: Patient-context tools (pre-chart, appointments, templates)
 * - Write: Document generation (referrals, summaries, handouts, certificates)
 */
export const EvidenceHomepageVerbs: EvidenceVerb[] = [
  {
    id: 'lookUp',
    labelKey: 'chat.verbs.lookUp',
    icon: Search,
    items: [
      {
        id: 'lookUp.dosing',
        patternLabelKey: 'chat.suggestions.lookUp.dosing.patternLabel',
        hoverPreviewKey: 'chat.suggestions.lookUp.dosing.hoverPreview',
        starterKey: 'chat.suggestions.lookUp.dosing.starter',
        locale: 'global',
        nonUsVariant: {
          patternLabelKey: 'chat.suggestions.lookUp.dosing.nonUs.patternLabel',
          hoverPreviewKey: 'chat.suggestions.lookUp.dosing.nonUs.hoverPreview',
          starterKey: 'chat.suggestions.lookUp.dosing.nonUs.starter',
        },
      },
      {
        id: 'lookUp.drugSafety',
        patternLabelKey: 'chat.suggestions.lookUp.drugSafety.patternLabel',
        hoverPreviewKey: 'chat.suggestions.lookUp.drugSafety.hoverPreview',
        starterKey: 'chat.suggestions.lookUp.drugSafety.starter',
        locale: 'global',
        nonUsVariant: {
          patternLabelKey: 'chat.suggestions.lookUp.drugSafety.nonUs.patternLabel',
          hoverPreviewKey: 'chat.suggestions.lookUp.drugSafety.nonUs.hoverPreview',
          starterKey: 'chat.suggestions.lookUp.drugSafety.nonUs.starter',
        },
      },
      {
        id: 'lookUp.guideline',
        patternLabelKey: 'chat.suggestions.lookUp.guideline.patternLabel',
        hoverPreviewKey: 'chat.suggestions.lookUp.guideline.hoverPreview',
        starterKey: 'chat.suggestions.lookUp.guideline.starter',
        locale: 'global',
      },
      {
        id: 'lookUp.tapering',
        patternLabelKey: 'chat.suggestions.lookUp.tapering.patternLabel',
        hoverPreviewKey: 'chat.suggestions.lookUp.tapering.hoverPreview',
        starterKey: 'chat.suggestions.lookUp.tapering.starter',
        locale: 'global',
        nonUsVariant: {
          patternLabelKey: 'chat.suggestions.lookUp.tapering.nonUs.patternLabel',
          hoverPreviewKey: 'chat.suggestions.lookUp.tapering.nonUs.hoverPreview',
          starterKey: 'chat.suggestions.lookUp.tapering.nonUs.starter',
        },
      },
      {
        id: 'lookUp.librarySearch',
        patternLabelKey: 'chat.suggestions.lookUp.librarySearch.patternLabel',
        hoverPreviewKey: 'chat.suggestions.lookUp.librarySearch.hoverPreview',
        starterKey: 'chat.suggestions.lookUp.librarySearch.starter',
        locale: 'global',
      },
    ],
  },
  {
    id: 'workUp',
    labelKey: 'chat.verbs.workUp',
    icon: Stethoscope,
    items: [
      {
        id: 'workUp.diagnosticWorkup',
        patternLabelKey: 'chat.suggestions.workUp.diagnosticWorkup.patternLabel',
        hoverPreviewKey: 'chat.suggestions.workUp.diagnosticWorkup.hoverPreview',
        starterKey: 'chat.suggestions.workUp.diagnosticWorkup.starter',
        locale: 'global',
        nonUsVariant: {
          patternLabelKey: 'chat.suggestions.workUp.diagnosticWorkup.nonUs.patternLabel',
          hoverPreviewKey: 'chat.suggestions.workUp.diagnosticWorkup.nonUs.hoverPreview',
          starterKey: 'chat.suggestions.workUp.diagnosticWorkup.nonUs.starter',
        },
      },
      {
        id: 'workUp.evaluateFinding',
        patternLabelKey: 'chat.suggestions.workUp.evaluateFinding.patternLabel',
        hoverPreviewKey: 'chat.suggestions.workUp.evaluateFinding.hoverPreview',
        starterKey: 'chat.suggestions.workUp.evaluateFinding.starter',
        locale: 'global',
        nonUsVariant: {
          patternLabelKey: 'chat.suggestions.workUp.evaluateFinding.nonUs.patternLabel',
          hoverPreviewKey: 'chat.suggestions.workUp.evaluateFinding.nonUs.hoverPreview',
          starterKey: 'chat.suggestions.workUp.evaluateFinding.nonUs.starter',
        },
      },
      {
        id: 'workUp.calculateScore',
        patternLabelKey: 'chat.suggestions.workUp.calculateScore.patternLabel',
        hoverPreviewKey: 'chat.suggestions.workUp.calculateScore.hoverPreview',
        starterKey: 'chat.suggestions.workUp.calculateScore.starter',
        locale: 'global',
        nonUsVariant: {
          patternLabelKey: 'chat.suggestions.workUp.calculateScore.nonUs.patternLabel',
          hoverPreviewKey: 'chat.suggestions.workUp.calculateScore.nonUs.hoverPreview',
          starterKey: 'chat.suggestions.workUp.calculateScore.nonUs.starter',
        },
      },
      {
        id: 'workUp.interpretResults',
        patternLabelKey: 'chat.suggestions.workUp.interpretResults.patternLabel',
        hoverPreviewKey: 'chat.suggestions.workUp.interpretResults.hoverPreview',
        starterKey: 'chat.suggestions.workUp.interpretResults.starter',
        locale: 'global',
        nonUsVariant: {
          patternLabelKey: 'chat.suggestions.workUp.interpretResults.nonUs.patternLabel',
          hoverPreviewKey: 'chat.suggestions.workUp.interpretResults.nonUs.hoverPreview',
          starterKey: 'chat.suggestions.workUp.interpretResults.nonUs.starter',
        },
      },
      {
        id: 'workUp.differential',
        patternLabelKey: 'chat.suggestions.workUp.differential.patternLabel',
        hoverPreviewKey: 'chat.suggestions.workUp.differential.hoverPreview',
        starterKey: 'chat.suggestions.workUp.differential.starter',
        locale: 'global',
        nonUsVariant: {
          patternLabelKey: 'chat.suggestions.workUp.differential.nonUs.patternLabel',
          hoverPreviewKey: 'chat.suggestions.workUp.differential.nonUs.hoverPreview',
          starterKey: 'chat.suggestions.workUp.differential.nonUs.starter',
        },
      },
    ],
  },
  {
    id: 'treat',
    labelKey: 'chat.verbs.treat',
    icon: Pill,
    items: [
      {
        id: 'treat.compareTreatments',
        patternLabelKey: 'chat.suggestions.treat.compareTreatments.patternLabel',
        hoverPreviewKey: 'chat.suggestions.treat.compareTreatments.hoverPreview',
        starterKey: 'chat.suggestions.treat.compareTreatments.starter',
        locale: 'global',
        nonUsVariant: {
          patternLabelKey: 'chat.suggestions.treat.compareTreatments.nonUs.patternLabel',
          hoverPreviewKey: 'chat.suggestions.treat.compareTreatments.nonUs.hoverPreview',
          starterKey: 'chat.suggestions.treat.compareTreatments.nonUs.starter',
        },
      },
      {
        id: 'treat.stepUpTherapy',
        patternLabelKey: 'chat.suggestions.treat.stepUpTherapy.patternLabel',
        hoverPreviewKey: 'chat.suggestions.treat.stepUpTherapy.hoverPreview',
        starterKey: 'chat.suggestions.treat.stepUpTherapy.starter',
        locale: 'global',
        nonUsVariant: {
          patternLabelKey: 'chat.suggestions.treat.stepUpTherapy.nonUs.patternLabel',
          hoverPreviewKey: 'chat.suggestions.treat.stepUpTherapy.nonUs.hoverPreview',
          starterKey: 'chat.suggestions.treat.stepUpTherapy.nonUs.starter',
        },
      },
      {
        id: 'treat.acuteTreatment',
        patternLabelKey: 'chat.suggestions.treat.acuteTreatment.patternLabel',
        hoverPreviewKey: 'chat.suggestions.treat.acuteTreatment.hoverPreview',
        starterKey: 'chat.suggestions.treat.acuteTreatment.starter',
        locale: 'global',
        nonUsVariant: {
          patternLabelKey: 'chat.suggestions.treat.acuteTreatment.nonUs.patternLabel',
          hoverPreviewKey: 'chat.suggestions.treat.acuteTreatment.nonUs.hoverPreview',
          starterKey: 'chat.suggestions.treat.acuteTreatment.nonUs.starter',
        },
      },
      {
        id: 'treat.firstLineMedication',
        patternLabelKey: 'chat.suggestions.treat.firstLineMedication.patternLabel',
        hoverPreviewKey: 'chat.suggestions.treat.firstLineMedication.hoverPreview',
        starterKey: 'chat.suggestions.treat.firstLineMedication.starter',
        locale: 'global',
        nonUsVariant: {
          patternLabelKey:
            'chat.suggestions.treat.firstLineMedication.nonUs.patternLabel',
          hoverPreviewKey:
            'chat.suggestions.treat.firstLineMedication.nonUs.hoverPreview',
          starterKey: 'chat.suggestions.treat.firstLineMedication.nonUs.starter',
        },
      },
      {
        id: 'treat.addOnTherapy',
        patternLabelKey: 'chat.suggestions.treat.addOnTherapy.patternLabel',
        hoverPreviewKey: 'chat.suggestions.treat.addOnTherapy.hoverPreview',
        starterKey: 'chat.suggestions.treat.addOnTherapy.starter',
        locale: 'global',
        nonUsVariant: {
          patternLabelKey: 'chat.suggestions.treat.addOnTherapy.nonUs.patternLabel',
          hoverPreviewKey: 'chat.suggestions.treat.addOnTherapy.nonUs.hoverPreview',
          starterKey: 'chat.suggestions.treat.addOnTherapy.nonUs.starter',
        },
      },
    ],
  },
  {
    id: 'prepare',
    labelKey: 'chat.verbs.prepare',
    icon: Sparkles,
    items: [
      {
        id: 'prepare.summarisePatient',
        patternLabelKey: 'chat.suggestions.prepare.summarisePatient.patternLabel',
        hoverPreviewKey: 'chat.suggestions.prepare.summarisePatient.hoverPreview',
        starterKey: 'chat.suggestions.prepare.summarisePatient.starter',
        locale: 'global',
        triggerSuffix: '@',
      },
      {
        id: 'prepare.showAppointments',
        patternLabelKey: 'chat.suggestions.prepare.showAppointments.patternLabel',
        hoverPreviewKey: 'chat.suggestions.prepare.showAppointments.hoverPreview',
        starterKey: 'chat.suggestions.prepare.showAppointments.starter',
        locale: 'global',
      },
      {
        id: 'prepare.reviewPatients',
        patternLabelKey: 'chat.suggestions.prepare.reviewPatients.patternLabel',
        hoverPreviewKey: 'chat.suggestions.prepare.reviewPatients.hoverPreview',
        starterKey: 'chat.suggestions.prepare.reviewPatients.starter',
        locale: 'global',
      },
      {
        id: 'prepare.findPatient',
        patternLabelKey: 'chat.suggestions.prepare.findPatient.patternLabel',
        hoverPreviewKey: 'chat.suggestions.prepare.findPatient.hoverPreview',
        starterKey: 'chat.suggestions.prepare.findPatient.starter',
        locale: 'global',
        triggerSuffix: '@',
      },
      {
        id: 'prepare.findTemplate',
        patternLabelKey: 'chat.suggestions.prepare.findTemplate.patternLabel',
        hoverPreviewKey: 'chat.suggestions.prepare.findTemplate.hoverPreview',
        starterKey: 'chat.suggestions.prepare.findTemplate.starter',
        locale: 'global',
      },
    ],
  },
  {
    id: 'write',
    labelKey: 'chat.verbs.write',
    icon: Pen,
    items: [
      {
        id: 'write.referral',
        patternLabelKey: 'chat.suggestions.write.referral.patternLabel',
        hoverPreviewKey: 'chat.suggestions.write.referral.hoverPreview',
        starterKey: 'chat.suggestions.write.referral.starter',
        locale: 'global',
        triggerSuffix: '@',
      },
      {
        id: 'write.patientSummary',
        patternLabelKey: 'chat.suggestions.write.patientSummary.patternLabel',
        hoverPreviewKey: 'chat.suggestions.write.patientSummary.hoverPreview',
        starterKey: 'chat.suggestions.write.patientSummary.starter',
        locale: 'global',
        triggerSuffix: '#',
      },
      {
        id: 'write.patientHandout',
        patternLabelKey: 'chat.suggestions.write.patientHandout.patternLabel',
        hoverPreviewKey: 'chat.suggestions.write.patientHandout.hoverPreview',
        starterKey: 'chat.suggestions.write.patientHandout.starter',
        locale: 'global',
      },
      {
        id: 'write.medicalCertificate',
        patternLabelKey: 'chat.suggestions.write.medicalCertificate.patternLabel',
        hoverPreviewKey: 'chat.suggestions.write.medicalCertificate.hoverPreview',
        starterKey: 'chat.suggestions.write.medicalCertificate.starter',
        locale: 'global',
      },
      {
        id: 'write.dischargeSummary',
        patternLabelKey: 'chat.suggestions.write.dischargeSummary.patternLabel',
        hoverPreviewKey: 'chat.suggestions.write.dischargeSummary.hoverPreview',
        starterKey: 'chat.suggestions.write.dischargeSummary.starter',
        locale: 'global',
        triggerSuffix: '@',
      },
    ],
  },
  {
    id: 'fill',
    labelKey: 'chat.verbs.fillForm',
    icon: FileText,
    items: [
      {
        id: 'fill.formFromSession',
        patternLabelKey: 'chat.suggestions.fill.formFromSession.patternLabel',
        hoverPreviewKey: 'chat.suggestions.fill.formFromSession.hoverPreview',
        starterKey: 'chat.suggestions.fill.formFromSession.starter',
        locale: 'global',
      },
      {
        id: 'fill.intakeForm',
        patternLabelKey: 'chat.suggestions.fill.intakeForm.patternLabel',
        hoverPreviewKey: 'chat.suggestions.fill.intakeForm.hoverPreview',
        starterKey: 'chat.suggestions.fill.intakeForm.starter',
        locale: 'global',
      },
      {
        id: 'fill.referralForm',
        patternLabelKey: 'chat.suggestions.fill.referralForm.patternLabel',
        hoverPreviewKey: 'chat.suggestions.fill.referralForm.hoverPreview',
        starterKey: 'chat.suggestions.fill.referralForm.starter',
        locale: 'global',
      },
    ],
  },
];
